import React, { FC, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';

import Api from '~/Api';
import BackButton from '~/components/atoms/buttons/BackButton';
import Button from '~/components/atoms/buttons/Button';
import Loader from '~/components/atoms/Loader';
import HookFormCheckbox from '~/components/hookFormControls/CheckboxControl';
import HookFormTextField from '~/components/hookFormControls/TextFieldControl';
import { useSnackBarContext } from '~/components/layoutComponents/snackbar/SnackbarContext';
import Tabs from '~/components/molecules/Tabs';
import TopHeadlineRow from '~/components/molecules/TopHeadlineRow';
import { READABLE_DATE_TIME_FORMAT } from '~/constants/date';
import { PlansListRoute } from '~/modules/plans';
import { PlanType } from '~/modules/plans/types';
import DateService from '~/services/Date';

import styles from './styles.module.scss';

type FormDataType = {
  name: string;
  is_visible: boolean;
  is_premium: boolean;
};

const formSchema = yup
  .object()
  .shape({
    name: yup.string().required('Required'),
    is_visible: yup.boolean().required('Required'),
    is_premium: yup.boolean().required('Required'),
  })
  .required();

const ThemeCategoriesItem: FC = () => {
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [plan, setPlan] = useState<PlanType>({
    id: '',
    name: '',
    is_visible: false,
    is_premium: false,
    days_count: 0,
    created_at: '2021-01-01',
    updated_at: '2021-01-01',
  });
  const { showSnackbar } = useSnackBarContext();
  const history = useHistory();

  const { id } = useParams<{ id: string }>();

  const form = useForm<FormDataType>({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      is_visible: false,
      is_premium: false,
    },
  });

  const { handleSubmit, setValue } = form;

  const loadTheme = async (id: string): Promise<void> => {
    try {
      setIsLoading(true);

      const plan = await Api.getPlan(id);
      setPlan(plan);

      setIsLoading(false);
    } catch (e: any) {
      showSnackbar(e.message);
      setIsLoading(false);
      history.push('/');
    }
  };

  const handleSuccessSubmit = async (data: FormDataType): Promise<void> => {
    try {
      setIsLoading(true);

      const plan = await Api.updatePlan(id, data);
      showSnackbar('Updated', 'success');

      setPlan(plan);
      setIsLoading(false);
    } catch (e: any) {
      showSnackbar(e.message);
      setIsLoading(false);
      history.push('/');
    }
  };

  useEffect(() => {
    if (!id) {
      history.push('/');
      return;
    }
    loadTheme(id).finally(() => setIsFirstLoading(false));
  }, [id]);

  useEffect(() => {
    setValue('name', plan.name);
    setValue('is_visible', plan.is_visible);
    setValue('is_premium', plan.is_premium);
  }, [plan]);

  return (
    <div>
      <BackButton
        className={styles.back_button}
        text="Back to List"
        onClick={(): void => {
          history.push(PlansListRoute.path);
        }}
      />
      <TopHeadlineRow className={styles.top_bar} text="Plan card" />
      <div className={styles.container}>
        <Loader isLoading={isFirstLoading}>
          <Tabs
            className={styles.container__tabs}
            items={[
              {
                label: 'General info',
                isActive: true,
              },
            ]}
            onClickItem={(): void => {}}
          />
          <div className={styles.form_container}>
            <FormProvider {...form}>
              <form>
                <div className={styles.form_container__field}>
                  <div style={{ marginBottom: '8px' }}>
                    <HookFormTextField
                      required
                      name="name"
                      label="Name"
                      fullWidth
                    />
                  </div>
                  <HookFormCheckbox name="is_visible" label="Is visible?" />
                  <HookFormCheckbox name="is_premium" label="Is premium?" />
                </div>
                <div className={styles.extra_info}>
                  <div className={styles.extra_info__title}>
                    <div>Created at:</div>
                    <div>Updated at:</div>
                  </div>
                  <div className={styles.extra_info__data}>
                    <div>
                      {DateService.format(
                        new Date(plan.created_at),
                        READABLE_DATE_TIME_FORMAT,
                      )}
                    </div>
                    <div>
                      {DateService.format(
                        new Date(plan.updated_at),
                        READABLE_DATE_TIME_FORMAT,
                      )}
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  color="primary"
                  variant="contained"
                  onClick={handleSubmit(handleSuccessSubmit)}
                >
                  Update info
                </Button>
              </form>
            </FormProvider>
          </div>
        </Loader>
      </div>
    </div>
  );
};

export default ThemeCategoriesItem;
